/**
 * SY-01 account linking: email magic link (in dev the API's mailbox is read directly),
 * Apple/Google through the adapters' identity providers (dev IdP in dev), and the conflict
 * chooser: when the identity already belongs to another save, both saves are shown and the
 * player keeps one. Removing a sign-in method (never the last one). Also the web → app
 * transfer token (SO-05, Flow G).
 */
import { SignInCancelled, isLinkConflict, linkIdentity, resolveLinkConflict, unlinkIdentity, type EmailMagicLink, type IdentityName, type IdentityProvider } from '@mp/adapters/auth';
import { HttpError, ROUTES, errorCode, isOffline, type ApiClient } from './api.js';
import type { Snapshot, TransferPreviewDto, TransferTokenDto } from './dto.js';
import type { PlayerStore } from './store.js';
import { reconcile, type SyncService } from './sync.js';

/** One save of a link conflict; `deleting`: that account is scheduled for deletion and cannot be kept. */
export type ConflictSide = { level: number; stars: number; deleting?: boolean };
export type Conflict = { conflict: true; current: ConflictSide; other: ConflictSide; resolveToken: string };
/**
 * A resolution the API refused because the situation changed under it (review #2 IDP-1..3):
 * the account to keep is being deleted, one of them was merged meanwhile, the sign-in method
 * moved, or another resolution used the token. The link starts again from the sign-in.
 */
export const RESOLVE_RESTART = ['account_deleting', 'already_merged', 'link_changed', 'resolve_token_expired'] as const;
export type ResolveOutcome = { kind: 'resolved' } | { kind: 'restart'; code: (typeof RESOLVE_RESTART)[number] };
export type LinkUiResult = { kind: 'linked'; provider: string } | { kind: 'conflict'; provider: string; conflict: Conflict } | { kind: 'cancelled' } | { kind: 'offline' } | { kind: 'error'; code: string };
/** `last`: the API refused because it is the account's only sign-in method (409 last_sign_in_method). */
export type UnlinkUiResult = { kind: 'removed'; provider: string } | { kind: 'last' } | { kind: 'offline' } | { kind: 'error'; code: string };
/** The sign-in methods a player can hold; `email` is linked by magic link, the rest by an IdP. */
export type SignInMethod = IdentityName | 'email';

export interface AccountDeps {
  api: ApiClient;
  store: PlayerStore;
  sync: SyncService;
  email: EmailMagicLink;
  identity: Partial<Record<IdentityName, IdentityProvider>>;
  now: () => number;
}

/** The magic-link token inside a dev mailbox message body. */
export function tokenFromMail(body: string): string | null {
  const m = /[?&]token=([A-Za-z0-9_-]{16,})/.exec(body);
  return m ? m[1]! : null;
}

export function looksLikeEmail(s: string): boolean {
  return /^[^\s@]{1,64}@[^\s@]{1,190}\.[^\s@]{2,}$/.test(s.trim());
}

/**
 * What a transfer link opens, for the confirmation (review #2 LNK-1): the account's summary
 * (`preview`, null when the server cannot say: an older server, offline), or `dead` when the
 * link opens nothing any more (expired, used, its session ended).
 */
export type TransferCheck = { kind: 'preview'; preview: TransferPreviewDto | null } | { kind: 'dead'; code: string };

/** What this device's save loses when it switches accounts. */
export type SaveAtRisk =
  /** an unlinked guest with progress: gone for good */
  | 'lost'
  /** linked to a sign-in: it stays with that account */
  | 'kept'
  /** a fresh save: nothing to lose */
  | 'none';

/** The preview route (GET /v1/transfer/:token/preview); switch it off here if a server lacks it. */
export const TRANSFER_PREVIEW = true;

export class AccountService {
  constructor(private d: AccountDeps) {}

  get linked(): string[] {
    return this.d.store.state.linked;
  }

  providers(): IdentityName[] {
    return (['apple', 'google', 'discord'] as const).filter((p) => this.d.identity[p]);
  }

  /** The saves of a conflict the player may keep: a side being deleted is never offered. */
  keepable(c: Conflict): ('current' | 'other')[] {
    return (['current', 'other'] as const).filter((k) => !c[k].deleting);
  }

  /** Mail a sign-in link. */
  async startEmail(email: string): Promise<void> {
    await this.d.sync.sync(); // link the save the player sees, with nothing pending
    await this.d.email.start(email.trim());
  }

  /** Dev only: every magic-link token in GET /v1/dev/mailbox for `email`, newest first. */
  async devMailboxTokens(email: string): Promise<string[]> {
    const r = await this.d.api.publicGet<{ messages: { subject: string; body: string; createdAt: number }[] }>(ROUTES.devMailbox(email.trim().toLowerCase()));
    return r.messages.map((m) => tokenFromMail(m.body)).filter((t): t is string => !!t);
  }

  async verifyEmail(token: string): Promise<LinkUiResult> {
    return this.outcome('email', () => this.d.email.verify(token));
  }

  async signIn(provider: IdentityName): Promise<LinkUiResult> {
    const idp = this.d.identity[provider];
    if (!idp) return { kind: 'error', code: 'unavailable' };
    await this.d.sync.sync();
    let tok;
    try {
      tok = await idp.signIn();
    } catch (e) {
      if (e instanceof SignInCancelled) return { kind: 'cancelled' };
      return { kind: 'error', code: (e as Error).message };
    }
    return this.outcome(provider, () => linkIdentity(this.d.api, tok));
  }

  private async outcome(provider: string, run: () => Promise<unknown>): Promise<LinkUiResult> {
    try {
      const r = await run();
      if (isLinkConflict(r)) return { kind: 'conflict', provider, conflict: r as unknown as Conflict };
      const snap = (r as { snapshot?: Snapshot }).snapshot;
      this.d.sync.adopt(snap);
      this.d.store.update((s) => {
        if (!s.linked.includes(provider)) s.linked = [...s.linked, provider];
      });
      return { kind: 'linked', provider };
    } catch (e) {
      if (isOffline(e)) return { kind: 'offline' };
      return { kind: 'error', code: errorCode(e) ?? (e as Error).message };
    }
  }

  /** Whether a linked method may be removed: only while another one is left (the API refuses the last one too). */
  canUnlink(): boolean {
    return this.linked.length > 1;
  }

  /**
   * Remove one sign-in method (DELETE /v1/auth/link/:provider). The API keeps the account's last one
   * (`last`), revokes a Sign in with Apple token of it, and tells the player in the inbox and by email.
   */
  async unlink(provider: SignInMethod): Promise<UnlinkUiResult> {
    try {
      const r = await unlinkIdentity(this.d.api, provider);
      this.d.sync.adopt((r as { snapshot?: Snapshot }).snapshot);
      this.d.store.update((s) => {
        s.linked = s.linked.filter((p) => p !== provider);
      });
      return { kind: 'removed', provider };
    } catch (e) {
      if (isOffline(e)) return { kind: 'offline' };
      const code = errorCode(e) ?? (e as Error).message;
      return code === 'last_sign_in_method' ? { kind: 'last' } : { kind: 'error', code };
    }
  }

  /**
   * Keep one save. Keeping the other one switches this device to it: the fresh session is
   * adopted, nothing queued for the retired save is sent, and its snapshot replaces ours.
   * `restart` when the API refused because things changed meanwhile (RESOLVE_RESTART): the
   * caller starts the link again; nothing changed on this device.
   */
  async resolve(c: Conflict, keep: 'current' | 'other', provider: string): Promise<ResolveOutcome> {
    let auth;
    try {
      auth = await resolveLinkConflict(this.d.api, c.resolveToken, keep);
    } catch (e) {
      const code = errorCode(e);
      const restart = RESOLVE_RESTART.find((r) => r === code);
      if (restart) return { kind: 'restart', code: restart };
      throw e;
    }
    this.d.api.adopt(auth);
    if (keep === 'other') this.d.store.update((s) => (s.outbox = []));
    const snap = await this.d.api.me();
    this.d.store.update((s) => {
      reconcile(s, snap, this.d.now());
      if (!s.linked.includes(provider)) s.linked = [...s.linked, provider];
    });
    return { kind: 'resolved' };
  }

  /** SO-05 web → app: a single-use token the app (or another device) claims. */
  transferToken(): Promise<TransferTokenDto> {
    return this.d.api.call<TransferTokenDto>('POST', ROUTES.transferToken, {});
  }

  /**
   * Which account a transfer link opens, before anything is claimed (the token stays unused).
   * A server without the preview route, or no network, gives no summary: the confirmation
   * still shows, just without it.
   */
  async checkTransfer(token: string): Promise<TransferCheck> {
    if (!TRANSFER_PREVIEW) return { kind: 'preview', preview: null };
    try {
      return { kind: 'preview', preview: await this.d.api.publicGet<TransferPreviewDto>(`/v1/transfer/${encodeURIComponent(token)}/preview`) };
    } catch (e) {
      // 404 `not_found` is a server without the route; `unknown_token` (404) and 410s mean the link is dead
      if (e instanceof HttpError && e.status < 500 && e.code !== 'not_found' && e.status !== 429) return { kind: 'dead', code: e.code };
      return { kind: 'preview', preview: null };
    }
  }

  /** What the save on this device loses if it switches to another account. */
  saveAtRisk(): SaveAtRisk {
    const s = this.d.store.state;
    if (s.linked.length) return 'kept';
    const progress = s.level > 1 || s.stars > 0 || Object.keys(s.levels).length > 0 || Object.values(s.shops).some((sh) => Object.keys(sh.upgrades).length > 0);
    return progress ? 'lost' : 'none';
  }

  /**
   * Claim a transfer link on this device: its save becomes this device's save. Only after the
   * player confirmed the switch (`confirm: true`; the server refuses a claim without it).
   */
  async claimTransfer(token: string): Promise<boolean> {
    try {
      const auth = await this.d.api.publicPost<{ playerId: string; accessToken: string; refreshToken: string; expiresIn: number }>('/v1/transfer/claim', { token, deviceId: this.d.store.state.deviceId, confirm: true });
      this.d.api.adopt(auth);
      this.d.store.update((s) => (s.outbox = []));
      const snap = await this.d.api.me();
      this.d.store.update((s) => reconcile(s, snap, this.d.now()));
      return true;
    } catch {
      return false;
    }
  }
}
